import type { ReactNode } from "react";
import { CircleHelp } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";

import { useLocalization } from "@i18n";

import type { InspectionParticipantUser } from "../../api/inspection";
import InspectionParticipantUserCombobox from "./InspectionParticipantUserCombobox";

export type ExtraParticipantSource = "name" | "gs3User";

export interface InspectionExtraParticipantFieldsProps {
  structureId: string;
  groupName: string;
  source: ExtraParticipantSource;
  onSourceChange: (source: ExtraParticipantSource) => void;
  name: string;
  onNameChange: (name: string) => void;
  nameError?: boolean;
  nameId: string;
  nameLabel?: string;
  selectedUserId: string;
  onUserChange: (userId: string, user: InspectionParticipantUser | null) => void;
  onNameSubmit?: () => void;
  requiredMark?: boolean;
  trailingAction?: ReactNode;
  excludedUserIds?: string[];
}

/**
 * Extra é um XOR (form §2.1 / QT-INSP-05-05): só o nome **ou** um usuário GS3.
 * Os dois campos não aparecem juntos e um não preenche o outro.
 */
const InspectionExtraParticipantFields = ({
  structureId,
  groupName,
  source,
  onSourceChange,
  name,
  onNameChange,
  nameError = false,
  nameId,
  nameLabel,
  selectedUserId,
  onUserChange,
  onNameSubmit,
  requiredMark = true,
  trailingAction,
  excludedUserIds,
}: InspectionExtraParticipantFieldsProps) => {
  const { translate } = useLocalization();

  const field = (
    source === "name" ? (
      <div className="insp-field">
        <label htmlFor={nameId}>
          {nameLabel ??
            translate("inspection.form.generalData.extraParticipantName")}
          {requiredMark ? (
            <>
              {" "}
              <span className="insp-req" aria-hidden>
                *
              </span>
            </>
          ) : null}
        </label>
        <input
          id={nameId}
          value={name}
          placeholder={translate("inspection.form.participantNamePlaceholder")}
          aria-invalid={nameError ? "true" : undefined}
          onChange={(event) => onNameChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onNameSubmit?.();
            }
          }}
          className="insp-inp"
        />
      </div>
    ) : (
      <InspectionParticipantUserCombobox
        structureId={structureId}
        value={selectedUserId}
        onChange={onUserChange}
        required={requiredMark}
        excludedUserIds={excludedUserIds}
      />
    )
  );

  return (
    <div className="insp-extra-fields">
      <div className="insp-field">
        <div className="insp-field-legend-row">
          <span id={`${groupName}-legend`} className="insp-field-legend">
            {translate("inspection.form.extraSourceLegend")}
          </span>
          <Tooltip
            arrow
            placement="top"
            title={
              <span className="insp-info-tip">
                <span>{translate("inspection.form.extraSourceHelpGs3")}</span>
                <span>{translate("inspection.form.extraSourceHelpExternal")}</span>
              </span>
            }
          >
            <button
              type="button"
              className="insp-info-btn"
              aria-label={translate("inspection.form.extraSourceHelpAria")}
            >
              <CircleHelp aria-hidden size={16} />
            </button>
          </Tooltip>
        </div>
        <div
          className="insp-choice"
          role="radiogroup"
          aria-labelledby={`${groupName}-legend`}
        >
          <button
            type="button"
            role="radio"
            aria-checked={source === "gs3User"}
            className={`insp-choice-opt${source === "gs3User" ? " is-selected" : ""}`}
            onClick={() => onSourceChange("gs3User")}
          >
            {translate("inspection.form.extraSourceGs3User")}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={source === "name"}
            className={`insp-choice-opt is-quiet${source === "name" ? " is-selected" : ""}`}
            onClick={() => onSourceChange("name")}
          >
            {translate("inspection.form.extraSourceName")}
          </button>
        </div>
      </div>

      {trailingAction ? (
        <div className="insp-create-extra-row">
          {field}
          {trailingAction}
        </div>
      ) : (
        field
      )}
    </div>
  );
};

export default InspectionExtraParticipantFields;
